import React, { useEffect, Fragment, useState, useRef } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";

import { GetDepartments } from "../../../../api/masters/departmentAPI";
import { GetCategoriesMedicalStore } from "../../../../api/medicalStore/master/categoryMedicalStoreAPI";
import { GetItems } from "../../../../api/medicalStore/master/itemAPI";
import { GetPurchaseBillById, UpdatePurchaseBill } from "../../../../api/medicalStore/transaction/purchaseBillAPI";
import { GetConsultants } from "../../../../api/masters/consultantAPI";
import { GetEstimateById, UpdateEstimate } from "../../../../api/medicalStore/transaction/estimateAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

// Validation schema using Yup
const schema = yup.object().shape({
  /* ================= HEADER ================= */
  vrType: yup.string().required("VR Type is required"),
  vrNo: yup
    .number()
    .typeError("VR No must be a number")
    .required("VR No is required"),
  date: yup.date().required("Date is required"),
  customerName: yup.string().required("Customer name is required"),
  doctorId: yup.string().required("Doctor is required"),
  departmentId: yup.string().required("Department is required"),

  grossAmount: yup
    .number()
    .typeError("Gross amount must be a number")
    .required("Gross amount is required"),
  additionPercentage: yup
    .number()
    .typeError("Addition % must be a number")
    .nullable(),
  additionAmount: yup
    .number()
    .typeError("Addition amount must be a number")
    .nullable(),
  discountPercentage: yup
    .number()
    .typeError("Discount % must be a number")
    .nullable(),
  discountAmount: yup
    .number()
    .typeError("Discount amount must be a number")
    .nullable(),
  roundOff: yup
    .number()
    .typeError("Round off must be a number")
    .nullable(),
  netAmount: yup
    .number()
    .typeError("Net amount must be a number")
    .required("Net amount is required"),

  /* ================= MEDICINE DETAILS ================= */
  medicineDetails: yup
    .array()
    .min(1, "At least one medicine is required")
    .of(
      yup.object().shape({
        categoryId: yup.string().required("Category is required"),

        productId: yup.string().required("Medicine is required"),

        expiryDate: yup
          .date()
          .typeError("Invalid expiry date")
          .required("Expiry date is required"),

        batchNo: yup
          .number()
          .typeError("Batch must be a number")
          .required("Batch number is required"),

        quantity: yup
          .number()
          .typeError("Quantity must be a number")
          .positive("Quantity must be greater than 0")
          .required("Quantity is required"),

        mrp: yup
          .number()
          .typeError("MRP must be a number")
          .positive("MRP must be greater than 0")
          .required("MRP is required"),

        saleRate: yup
          .number()
          .typeError("Rate must be a number")
          .positive("Rate must be greater than 0")
          .required("Rate is required"),

        amount: yup
          .number()
          .typeError("Amount must be a number")
          .required("Amount is required"),

        discountPercentage: yup
          .number()
          .typeError("Discount % must be a number")
          .min(0)
          .nullable(),

        discountAmount: yup
          .number()
          .typeError("Discount amount must be a number")
          .min(0)
          .nullable(),

        gstPercentage: yup
          .number()
          .typeError("GST % must be a number")
          .min(0)
          .nullable(),

        gstAmount: yup
          .number()
          .typeError("GST amount must be a number")
          .min(0)
          .nullable(),

        totalAmount: yup
          .number()
          .typeError("Total amount must be a number")
          .required("Total amount is required")
      })
    )
});

const EditEstimateEntry = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();

  const [departments, setDepartments] = useState([]);
    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState([]);
    const [doctors, setDoctors] = useState([]);
  
    const {
      register,
      control,
      setValue,
      getValues,
      handleSubmit,
      formState: { errors }
    } = useForm({
      resolver: yupResolver(schema),
      defaultValues: {
        medicineDetails: [{}]
      }
    });
  
    const { fields, append, remove } = useFieldArray({
      control,
      name: "medicineDetails"
    });


  /* ================= ROW CALC ================= */
    const calculateRow = index => {
      const qty = +getValues(`medicineDetails.${index}.quantity`) || 0;
      const rate = +getValues(`medicineDetails.${index}.saleRate`) || 0;
      const disPct = +getValues(`medicineDetails.${index}.discountPercentage`) || 0;
      const gstPct = +getValues(`medicineDetails.${index}.gstPercentage`) || 0;
  
      const amount = qty * rate;
      const disAmt = (amount * disPct) / 100;
      const taxable = amount - disAmt;
      const gstAmt = (taxable * gstPct) / 100;
      const total = taxable + gstAmt;
  
      setValue(`medicineDetails.${index}.amount`, amount.toFixed(2));
      setValue(`medicineDetails.${index}.discountAmount`, disAmt.toFixed(2));
      setValue(`medicineDetails.${index}.gstAmount`, gstAmt.toFixed(2));
      setValue(`medicineDetails.${index}.totalAmount`, total.toFixed(2));
    };
  
    /* ================= SUMMARY ================= */
    const medicines = useWatch({ control, name: "medicineDetails" });
    const additionPercentage = useWatch({ control, name: "additionPercentage" });
    const discountPercentage = useWatch({ control, name: "discountPercentage" });
    const roundOff = useWatch({ control, name: "roundOff" });
  
    useEffect(() => {
      let gross = 0, gst = 0, disc = 0;
  
      medicines?.forEach(m => {
        gross += +m.amount || 0;
        gst += +m.gstAmount || 0;
        disc += +m.discountAmount || 0;
      });
  
      const addAmt = (gross * (+additionPercentage || 0)) / 100;
      const billDisc = (gross * (+discountPercentage || 0)) / 100;
      const net = gross + gst + addAmt - billDisc;
  
      setValue("grossAmount", gross.toFixed(2));
      setValue("additionAmount", addAmt.toFixed(2));
      setValue("discountAmount", billDisc.toFixed(2));
      setValue("netAmount", net.toFixed(roundOff || 0));
    }, [medicines, additionPercentage, discountPercentage, roundOff, setValue]);

  const fetchData = async () => {
    try {
      const response = await GetEstimateById(id); // Make sure login function returns token
      
      if (response.success) {
        const data = response.data;
        // ================= SIMPLE FIELDS =================
        Object.keys(data).forEach(key => {
          if (key === "medicineDetails") return;

          // handle date fields if any in future
          if (data[key] instanceof Date || typeof data[key] === "string") {
            if (key.includes("date") && data[key]) {
              setValue(key, data[key].split("T")[0]);
              return;
            }
          }

          setValue(key, data[key]);
        });

        // ================= MEDICINE DETAILS (ARRAY) =================
        if (Array.isArray(data.medicineDetails)) {
          data.medicineDetails.forEach((item, index) => {
            Object.keys(item).forEach(field => {
              if (field === "expiryDate" && item[field]) {
                setValue(
                  `medicineDetails.${index}.expiryDate`,
                  item[field].split("T")[0]
                );
              } else {
                setValue(
                  `medicineDetails.${index}.${field}`,
                  item[field]
                );
              }
            });
          });
        }
      } else {
        // setError(response.message);
        showToast("error", response.message);
      }
    } catch (error) {
      // setError(error); // Handle login errors
      showToast("error", error);
    }
  };

  useEffect(() => {
    if (!id) return;
    // Fetch existing data
    const loadAllData = async () => {
      try {
        const [
          departmentRes,
          categoryRes,
          itemRes,
          consultantRes
        ] = await Promise.all([
          GetDepartments(),
          GetCategoriesMedicalStore(),
          GetItems(),
          GetConsultants()
        ]);

        if (departmentRes.success) {
          setDepartments(departmentRes.data);
        } else {
          showToast("error", departmentRes.message);
        }

        if (categoryRes.success) {
          setCategories(categoryRes.data);
        } else {
          showToast("error", categoryRes.message);
        }

        if (itemRes.success) {
          setItems(itemRes.data);
        } else {
          showToast("error", itemRes.message);
        }

        if (consultantRes.success) {
          setDoctors(consultantRes.data);
        } else {
          showToast("error", consultantRes.message);
        }

        // ✅ CALL AFTER ALL ABOVE ARE DONE
        fetchData();
      } catch (error) {
        console.error("Error loading master data:", error);
      }
    };
    loadAllData();
  }, [id]);


  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });

      const response = await UpdateEstimate(id, data);
      if (response.success) {
        showToast("success", response.message);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
		useKeyboardSubmit(handleSubmit, onSubmit);
		useEnterNavigation(formRef);

  return (
    <Fragment>
			<Breadcrumb parent="Medical Store" title="Edit Estimate" />

			<div className="container-fluid">
				<div className="card">
					<div className="card-header d-flex justify-content-between">
						<h5>Edit Estimate Entry</h5>
						<Link
							to={`/medical-store/transaction/estimate-entry/list`}
						>
							<button className="btn btn-primary">List Estimate</button>
						</Link>
					</div>
					<form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
						<div className="card-body">

							{/* HEADER */}
							<div className="row">
								<div className="col-3">
									<label>VR Type</label>
									<input className="form-control" {...register("vrType")} />
									{errors.vrType && <span className="text-danger">{errors.vrType.message}</span>}
								</div>
								<div className="col-3">
									<label>VR No</label>
									<input type="number" className="form-control" {...register("vrNo")} />
									{errors.vrNo && <span className="text-danger">{errors.vrNo.message}</span>}
								</div>
								<div className="col-3">
									<label>Date</label>
									<input type="date" className="form-control" {...register("date")} />
									{errors.date && <span className="text-danger">{errors.date.message}</span>}
								</div>
								<div className="col-3">
									<label>Customer Name</label>
									<input className="form-control" {...register("customerName")} />
									{errors.customerName && <span className="text-danger">{errors.customerName.message}</span>}
								</div>
							</div>

							<div className="row mt-3">
								<div className="col-3">
									<label>Doctor</label>
									<select className="form-control" {...register("doctorId")}>
										<option value="">Select</option>
										{doctors.map(d => (
											<option key={d._id} value={d._id}>{d.name}</option>
										))}
									</select>
									{errors.doctorId && <span className="text-danger">{errors.doctorId.message}</span>}
								</div>
								<div className="col-3">
									<label>Department</label>
									<select className="form-control" {...register("departmentId")}>
										<option value="">Select</option>
										{departments.map(d => (
											<option key={d._id} value={d._id}>{d.name}</option>
										))}
									</select>
									{errors.departmentId && <span className="text-danger">{errors.departmentId.message}</span>}
								</div>
							</div>

							{/* MEDICINES */}
							<hr />
							<h6>Medicine Details</h6>

							{fields.map((f, i) => (
								<div className="row border p-2 mb-2 align-items-end" key={f.id}>
									{/* Category */}
									<div className="col-2">
										<label>Category</label>
										<select
											className="form-control"
											{...register(`medicineDetails.${i}.categoryId`)}
										>
											<option value="">Select</option>
											{categories.map(c => (
												<option key={c._id} value={c._id}>{c.name}</option>
											))}
										</select>
										{errors?.medicineDetails?.[i]?.categoryId &&
											<span className="text-danger">
												{errors.medicineDetails[i].categoryId.message}
											</span>}
									</div>

									{/* Medicine */}
									<div className="col-2">
										<label>Medicine</label>
										<select
											className="form-control"
											{...register(`medicineDetails.${i}.productId`)}
										>
											<option value="">Select</option>
											{items.map(p => (
												<option key={p._id} value={p._id}>{p.name}</option>
											))}
										</select>
										{errors?.medicineDetails?.[i]?.productId &&
											<span className="text-danger">
												{errors.medicineDetails[i].productId.message}
											</span>}
									</div>

									{/* Expiry */}
									<div className="col-2">
										<label>Expiry Date</label>
										<input
											type="date"
											className="form-control"
											{...register(`medicineDetails.${i}.expiryDate`)}
										/>
										{errors?.medicineDetails?.[i]?.expiryDate &&
											<span className="text-danger">
												{errors.medicineDetails[i].expiryDate.message}
											</span>}
									</div>

									{/* Batch */}
									<div className="col-2">
										<label>Batch</label>
										<input
											type="number"
											className="form-control"
											{...register(`medicineDetails.${i}.batchNo`)}
										/>
										{errors?.medicineDetails?.[i]?.batchNo &&
											<span className="text-danger">
												{errors.medicineDetails[i].batchNo.message}
											</span>}
									</div>

									{/* Quantity */}
									<div className="col-2">
										<label>Qty</label>
										<input
											type="number" step="any"
											className="form-control"
											{...register(`medicineDetails.${i}.quantity`, {
												onChange: () => calculateRow(i)
											})}
										/>
										{errors?.medicineDetails?.[i]?.quantity &&
											<span className="text-danger">
												{errors.medicineDetails[i].quantity.message}
											</span>}
									</div>

									{/* MRP */}
									<div className="col-2">
										<label>MRP</label>
										<input
											type="number" step="any"
											className="form-control"
											{...register(`medicineDetails.${i}.mrp`)}
										/>
										{errors?.medicineDetails?.[i]?.mrp &&
											<span className="text-danger">
												{errors.medicineDetails[i].mrp.message}
											</span>}
									</div>

									{/* Sale Rate */}
									<div className="col-2">
										<label>Rate</label>
										<input
											type="number" step="any"
											className="form-control"
											{...register(`medicineDetails.${i}.saleRate`, {
												onChange: () => calculateRow(i)
											})}
										/>
										{errors?.medicineDetails?.[i]?.saleRate &&
											<span className="text-danger">
												{errors.medicineDetails[i].saleRate.message}
											</span>}
									</div>

									{/* Amount */}
									<div className="col-2">
										<label>Amount</label>
										<input
											type="number" step="any"
											className="form-control"
											readOnly
											{...register(`medicineDetails.${i}.amount`)}
										/>
										{errors?.medicineDetails?.[i]?.amount &&
											<span className="text-danger">
												{errors.medicineDetails[i].amount.message}
											</span>}
									</div>

									{/* Discount % */}
									<div className="col-2">
										<label>Disc %</label>
										<input
											type="number" step="any"
											className="form-control"
											{...register(`medicineDetails.${i}.discountPercentage`, {
												onChange: () => calculateRow(i)
											})}
										/>
										{errors?.medicineDetails?.[i]?.discountPercentage &&
											<span className="text-danger">
												{errors.medicineDetails[i].discountPercentage.message}
											</span>}
									</div>

									{/* Discount Amount */}
									<div className="col-2">
										<label>Disc Amt</label>
										<input
											type="number" step="any"
											className="form-control"
											readOnly
											{...register(`medicineDetails.${i}.discountAmount`)}
										/>
										{errors?.medicineDetails?.[i]?.discountAmount &&
											<span className="text-danger">
												{errors.medicineDetails[i].discountAmount.message}
											</span>}
									</div>

									{/* GST % */}
									<div className="col-2">
										<label>GST %</label>
										<input
											type="number" step="any"
											className="form-control"
											{...register(`medicineDetails.${i}.gstPercentage`, {
												onChange: () => calculateRow(i)
											})}
										/>
										{errors?.medicineDetails?.[i]?.gstPercentage &&
											<span className="text-danger">
												{errors.medicineDetails[i].gstPercentage.message}
											</span>}
									</div>

									{/* GST Amount */}
									<div className="col-2">
										<label>GST Amt</label>
										<input
											type="number" step="any"
											className="form-control"
											readOnly
											{...register(`medicineDetails.${i}.gstAmount`)}
										/>
										{errors?.medicineDetails?.[i]?.gstAmount &&
											<span className="text-danger">
												{errors.medicineDetails[i].gstAmount.message}
											</span>}
									</div>

									{/* Total */}
									<div className="col-2">
										<label>Total</label>
										<input
											type="number" step="any"
											className="form-control"
											readOnly
											{...register(`medicineDetails.${i}.totalAmount`)}
										/>
										{errors?.medicineDetails?.[i]?.totalAmount &&
											<span className="text-danger">
												{errors.medicineDetails[i].totalAmount.message}
											</span>}
									</div>

									{/* Remove */}
									<div className="col-2 text-center">
										<button
											type="button"
											className="btn btn-danger"
											onClick={() => remove(i)}
										>
											✕
										</button>
									</div>

								</div>
							))}


							<button type="button" className="btn btn-secondary" onClick={() => append({})}>
								+ Add Medicine
							</button>

							{/* SUMMARY */}
							<hr />
							<div className="row">
								<div className="col-3">
									<label>Gross Amount</label>
									<input type="number" step="any" className="form-control" {...register("grossAmount")} />
								</div>
								<div className="col-3">
									<label>Addition %</label>
									<input type="number" step="any" className="form-control" {...register("additionPercentage")} />
								</div>
								<div className="col-3">
									<label>Addition Amount</label>
									<input type="number" step="any" className="form-control" {...register("additionAmount")} />
								</div>
								<div className="col-3">
									<label>Discount %</label>
									<input type="number" step="any" className="form-control" {...register("discountPercentage")} />
								</div>
								<div className="col-3">
									<label>Discount Amount</label>
									<input type="number" step="any" className="form-control" {...register("discountAmount")} />
								</div>
								<div className="col-3">
									<label>Round Off</label>
									<input type="number" step="any" className="form-control" {...register("roundOff")} />
								</div>
								<div className="col-3">
									<label>Net Amount</label>
									<input type="number" step="any" className="form-control" {...register("netAmount")} />
								</div>
							</div>

						</div>

						<div className="card-footer text-end">
							<button className="btn btn-primary">Submit</button>
						</div>
					</form>
				</div>
			</div>
		</Fragment>
  );
};

export default EditEstimateEntry;
